import React, { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_MY_CLASS,
  ENSURE_CLASS_CODE,
} from '../graphql/class-queries';

const MODES = [
  { key: 'classic', num: '01', han: '学', title: 'Study', desc: 'Spaced-repetition flashcards. Calm and focused.', battle: false },
  { key: 'paragraph', num: '02', han: '读', title: 'Reading', desc: 'Read a passage, then translate from understanding.', battle: false },
  { key: 'quiz-battle', num: '03', han: '战', title: 'Battle', desc: 'Multiplayer quiz. Race friends to answer.', battle: true },
];

/**
 * TeacherHome — landing page after sign-in. Surfaces the two features that
 * were previously hidden (class code under Settings, pack installation only
 * during onboarding) above the existing mode picker.
 */
function TeacherHome({
  teacher,
  wordCount,
  onSelectMode,
  onOpenPacks,
  onOpenWords,
  onOpenSettings,
}) {
  const { data, loading } = useQuery(GET_MY_CLASS, {
    fetchPolicy: 'cache-and-network',
  });
  const [ensureCode] = useMutation(ENSURE_CLASS_CODE, {
    refetchQueries: [{ query: GET_MY_CLASS }],
  });
  const [copied, setCopied] = useState(false);

  // Auto-create the class code on first visit so it's always copyable.
  useEffect(() => {
    if (loading) return;
    if (data?.myClass?.code) return;
    ensureCode().catch(() => { /* surfaced via Settings on retry */ });
  }, [loading, data, ensureCode]);

  const cls = data?.myClass;
  const code = cls?.code;
  const limit = cls?.studentLimit ?? (teacher?.plan === 'pro' ? 40 : 4);
  const enrolled = cls?.students?.length ?? 0;
  const joinUrl = code ? `${window.location.origin}/class/${code}` : '';

  const handleCopy = useCallback(async () => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* user can copy from Settings */ }
  }, [joinUrl]);

  // Mirror ModeSelector's keyboard shortcuts (1/2/3) so tile-numpad still works.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= MODES.length) {
        onSelectMode(MODES[num - 1].key);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onSelectMode]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          padding: 'clamp(48px, 6vw, 80px) clamp(20px, 4vw, 80px) 24px',
        }}
      >
        {/* Hero */}
        <div style={{ marginBottom: 28 }}>
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>
            Teacher home · 老师主页
          </div>
          <h1
            className="t-display"
            style={{ fontFamily: 'var(--font-han)', fontWeight: 500, marginBottom: 4 }}
          >
            欢迎回来
          </h1>
          <p className="t-body" style={{ fontSize: 17 }}>
            Welcome back{teacher?.name ? `, ${teacher.name.split(' ')[0]}` : ''}.
            {' '}
            {wordCount > 0 ? `${wordCount} characters ready.` : 'Add your first words below.'}
          </p>
        </div>

        {/* Cards: class + library */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 16,
            marginBottom: 40,
          }}
        >
          {/* Class card */}
          <section
            className="card"
            style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 className="t-h3">Class · 班级</h2>
              <span className="t-small">{enrolled} / {limit} students</span>
            </div>
            {code ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: 'var(--paper-2)',
                    border: '1px solid var(--rule)',
                    borderRadius: 10,
                    padding: '12px 16px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-eyebrow" style={{ marginBottom: 4 }}>Code</div>
                    <div
                      className="t-mono"
                      style={{ fontSize: 24, letterSpacing: '0.16em', fontWeight: 500 }}
                    >
                      {code}
                    </div>
                  </div>
                  <button
                    className="btn btn--sm"
                    onClick={handleCopy}
                    disabled={!joinUrl}
                    aria-live="polite"
                  >
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                </div>
                <p className="t-small" style={{ wordBreak: 'break-all', margin: 0 }}>
                  {joinUrl}
                </p>
                <p className="t-small" style={{ margin: 0, color: 'var(--ink-3)' }}>
                  Share this link so students can study your library on their own devices.
                </p>
                <div>
                  <button className="btn btn--ghost btn--sm" onClick={onOpenSettings}>
                    Manage roster →
                  </button>
                </div>
              </>
            ) : (
              <p className="t-small">Setting up your class code…</p>
            )}
          </section>

          {/* Library card */}
          <section
            className="card"
            style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 className="t-h3">Library · 词库</h2>
              <span className="t-small">
                {teacher?.plan === 'pro' ? 'Pro · unlimited' : 'Free · cap 50'}
              </span>
            </div>
            <div
              style={{
                background: 'var(--paper-2)',
                border: '1px solid var(--rule)',
                borderRadius: 10,
                padding: '12px 16px',
              }}
            >
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>Words</div>
              <div className="t-mono" style={{ fontSize: 24, fontWeight: 500 }}>
                {wordCount}
              </div>
            </div>
            <p className="t-small" style={{ margin: 0, color: 'var(--ink-3)' }}>
              Add a curated HSK pack or build your library from scratch.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn--sm" onClick={onOpenPacks}>
                Browse packs →
              </button>
              <button className="btn btn--ghost btn--sm" onClick={onOpenWords}>
                Manage words
              </button>
            </div>
          </section>
        </div>

        {/* Mode tiles */}
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          Study modes · 学习模式
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
            marginBottom: 32,
          }}
        >
          {MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => onSelectMode(m.key)}
              className="ms-tile"
              style={{
                position: 'relative',
                background: m.battle ? 'var(--battle-bg)' : 'var(--paper)',
                border: m.battle ? '1px solid var(--battle-bg)' : '1px solid var(--rule)',
                borderRadius: 12,
                padding: 24,
                paddingBottom: 20,
                textAlign: 'left',
                cursor: 'pointer',
                color: m.battle ? 'var(--battle-fg)' : 'var(--ink)',
                transition: 'transform .15s, box-shadow .15s',
                minHeight: 240,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                fontFamily: 'var(--font-sans)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--sh3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <div>
                <span
                  className="t-mono"
                  style={{ fontSize: 12, letterSpacing: '0.14em', opacity: 0.6 }}
                >
                  {m.num}
                </span>
                <div
                  className="ms-tile-han"
                  style={{
                    fontFamily: 'var(--font-han)',
                    fontSize: 80,
                    lineHeight: 0.95,
                    letterSpacing: '0.04em',
                    margin: '20px 0 16px',
                    color: m.battle ? 'var(--paper)' : 'var(--ink)',
                  }}
                >
                  {m.han}
                </div>
              </div>
              <div>
                <h2 className="t-h3" style={{ marginBottom: 6 }}>{m.title}</h2>
                <p
                  className="t-small"
                  style={{ color: m.battle ? '#c8c1b0' : 'var(--ink-3)' }}
                >
                  {m.desc}
                </p>
              </div>
              {m.battle && (
                <div
                  style={{
                    position: 'absolute',
                    top: 18,
                    right: 18,
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: 'var(--vermillion)',
                    boxShadow: '0 0 0 4px rgba(216,58,44,0.2)',
                  }}
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TeacherHome;
